import type { RightyTurnEvent } from '../types'
import type { PusherConnection } from '@dashboard/services/realtimeConnectionMonitor'

export interface TurnStreamObserver {
  replay: () => Promise<void>
  stop: () => void
}

function parseReplay(text: string): RightyTurnEvent[] {
  return text
    .replace(/\r\n/g, '\n')
    .split('\n\n')
    .flatMap((frame) => {
      const lines = frame.split('\n')
      const type = lines
        .find((line) => line.startsWith('event:'))
        ?.slice(6)
        .trim()
      // The SSE id is the journal sequence; a payload may carry its own `sequence` (provider_step).
      const sequence = Number(
        lines
          .find((line) => line.startsWith('id:'))
          ?.slice(3)
          .trim()
      )
      const data = lines
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trim())
        .join('\n')
      if (!type || !data || !Number.isInteger(sequence)) {
        return []
      }
      const { sequence: _overwritten, ...payload } = JSON.parse(data) as Record<string, unknown>
      return [{ sequence, type, payload }]
    })
}

/** Both transports feed the same contiguous cursor; only applied events advance it. */
export function observeTurnStream(options: {
  teamPid: string
  threadId: number
  turnId: string
  cursor: () => number
  apply: (event: RightyTurnEvent) => void
  onError: () => void
}): TurnStreamObserver {
  const buffered = new Map<number, RightyTurnEvent>()
  const abort = new AbortController()
  let lastEventAt = Date.now()
  let gapTimer: number | undefined
  let replaying: Promise<void> | undefined
  let replayAgain = false
  const echo = window.Echo
  const channelName = `righty-thread.${options.threadId}`
  const connection = echo?.connector?.pusher?.connection as PusherConnection | undefined

  function receive(event: RightyTurnEvent) {
    if (abort.signal.aborted || !Number.isInteger(event.sequence) || event.sequence <= options.cursor()) {
      return
    }
    lastEventAt = Date.now()
    buffered.set(event.sequence, event)
    let next = buffered.get(options.cursor() + 1)
    while (next && !abort.signal.aborted) {
      buffered.delete(next.sequence)
      options.apply(next)
      next = buffered.get(options.cursor() + 1)
    }
    if (buffered.size === 0) {
      clearTimeout(gapTimer)
      gapTimer = undefined
    } else if (!gapTimer) {
      gapTimer = window.setTimeout(() => {
        gapTimer = undefined
        void replay()
      }, 1000)
    }
  }

  function replay(): Promise<void> {
    if (abort.signal.aborted) {
      return Promise.resolve()
    }
    if (replaying) {
      replayAgain = true
      return replaying
    }
    replaying = (async () => {
      try {
        const response = await fetch(
          `/api/${options.teamPid}/righty/turns/${options.turnId}/events?cursor=${options.cursor()}`,
          {
            credentials: 'include',
            headers: { Accept: 'text/event-stream' },
            signal: abort.signal
          }
        )
        if (!response.ok) {
          throw new Error('Turn replay unavailable')
        }
        for (const event of parseReplay(await response.text())) {
          receive(event)
        }
      } catch {
        if (!abort.signal.aborted) {
          options.onError()
        }
      } finally {
        replaying = undefined
        if (replayAgain) {
          replayAgain = false
          void replay()
        }
      }
    })()
    return replaying
  }

  const onStateChange = ({ current }: { current?: unknown }) => {
    if (current === 'connected') {
      void replay()
    }
  }
  connection?.bind?.('state_change', onStateChange)
  echo
    ?.private(channelName)
    .listen('.RightyTurnEvent', (event: RightyTurnEvent & { turnId: string }) => {
      if (event.turnId === options.turnId) {
        receive(event)
      }
    })
    .subscribed(() => {
      void replay()
    })

  const safetyTimer = setInterval(() => {
    if (Date.now() - lastEventAt >= 5000) {
      lastEventAt = Date.now()
      void replay()
    }
  }, 5000)

  return {
    replay,
    stop() {
      abort.abort()
      clearTimeout(gapTimer)
      clearInterval(safetyTimer)
      buffered.clear()
      connection?.unbind?.('state_change', onStateChange)
      echo?.leave(channelName)
    }
  }
}
